// Permanent customer deletion (platform administrators; the server additionally requires a
// Microsoft Entra session). The dialog shows the server-computed impact and requires the exact
// customer name; the server checks the name again.
import { useState } from 'react';
import { ApiError } from '../../services/tenancy';
import { deleteCustomer, previewCustomerDelete, type CustomerDeleteImpact } from '../../services/tenancy';
import { customerNameConfirmed, deletedNotice, describeCustomerDelete } from './lifecycleModel';

const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';
const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm w-full';

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.code ? `${err.message} (${err.code})` : err.message;
  return 'The request could not be completed. Please try again.';
}

export function CustomerDeleteDialog({ impact, typed, busy, onTyped, onCancel, onConfirm }: {
  impact: CustomerDeleteImpact; typed: string; busy?: boolean;
  onTyped: (v: string) => void; onCancel: () => void; onConfirm: () => void;
}) {
  const d = describeCustomerDelete(impact);
  const ready = !d.blocked && customerNameConfirmed(typed, impact.customer.name);
  return (
    <div data-management-modal role="dialog" aria-modal="true" aria-labelledby="customer-delete-title" className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <form className="bg-gray-800 border border-red-700 rounded-lg w-full max-w-lg p-5 space-y-4"
        onSubmit={(e) => { e.preventDefault(); if (ready && !busy) onConfirm(); }}>
        <h3 id="customer-delete-title" className="text-lg font-semibold text-red-200">{d.title}</h3>
        {d.blocked
          ? <p role="alert" className="text-sm text-yellow-200">{d.blocked}</p>
          : <ul className="list-disc pl-5 space-y-1 text-sm">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>}
        {!d.blocked && (
          <div>
            <label htmlFor="customer-delete-confirm" className="block text-xs text-gray-300 mb-1">
              Type the customer name <span className="font-mono text-gray-100">{impact.customer.name}</span> to confirm
            </label>
            <input id="customer-delete-confirm" className={input} value={typed} autoComplete="off" autoFocus
              onChange={(e) => onTyped(e.target.value)} />
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={onCancel} disabled={busy}>Cancel</button>
          {!d.blocked && (
            <button type="submit" className={`${btn} bg-red-700 hover:bg-red-600`} disabled={!ready || busy}>
              {busy ? 'Deleting…' : d.confirm}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/** "Delete customer" card of the customer page (platform administrators). */
export default function CustomerDeleteCard({ tenant, onDeleted, onError }: {
  tenant: { id: string; name: string }; onDeleted: (notice: string) => void; onError: (message: string) => void;
}) {
  const [impact, setImpact] = useState<CustomerDeleteImpact | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      setTyped('');
      setImpact(await previewCustomerDelete(tenant.id));
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const res = await deleteCustomer(tenant.id, typed.trim());
      setImpact(null);
      onDeleted(deletedNotice('customer', { customer: res.impact }));
    } catch (err) {
      setImpact(null);
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-gray-800 border border-red-900 rounded p-4 space-y-2">
      <h3 className="font-semibold">Delete customer</h3>
      <p className="text-xs text-gray-400">
        Permanently deletes this customer with its networks, departments and users. Its radios return to the rental inventory.
        Disable or detach instead if the customer should be kept.
      </p>
      <button className={`${btn} bg-red-800 hover:bg-red-700`} disabled={busy} onClick={open}>Delete customer…</button>
      {impact && <CustomerDeleteDialog impact={impact} typed={typed} busy={busy} onTyped={setTyped} onCancel={() => setImpact(null)} onConfirm={confirm} />}
    </div>
  );
}
